import { forwardRef } from 'react';
import cn from 'classnames';

// Дополнительные props для целевого Component (через `as`) принимаются как rest
// и spread'ятся в Component — TS не валидирует props для React.ElementType, поэтому
// явного индекс-сигнатура не нужна. (В оригинале mcms-front было `[key: string]: any`,
// но это ломает типизацию именованных полей variant/shape — TS считает их `any`.)
export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLElement>, 'type'> {
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLElement>;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'dark' | 'transparent';
  shape?: 'square' | 'rect';
  disabled?: boolean;
  className?: string;
  isLoading?: boolean;
  as?: React.ElementType;
  // Когда as={Link}/<a>, Button рендерится ссылкой — `to` пробрасывается в rest.
  // Нужно для нативного ctrl/⌘/средний-клик «открыть в новой вкладке».
  to?: string;
}

export const Button = forwardRef<HTMLElement, ButtonProps>(
  (
    {
      children,
      onClick,
      type = 'button',
      variant = 'primary',
      shape = 'rect',
      disabled = false,
      className,
      isLoading,
      as: Component = 'button',
      ...rest
    },
    ref
  ) => {
    // text-sm + font-medium + leading-5 = 14px/500/20px по Figma-спецификации
    // кнопок. Раньше было text-base (16px/22px) — не совпадало с дизайном.
    const baseClasses =
      'relative inline-flex items-center justify-center text-sm font-medium leading-5 transition duration-200 ease-in-out';
    const variants = {
      primary:
        'bg-action-primary text-content-inverse hover:bg-action-primary-hover active:bg-action-primary-active',
      secondary: 'bg-action-secondary text-content-primary hover:bg-action-secondary-hover',
      dark: 'bg-action-primary-active text-content-inverse',
      transparent:
        'text-action-primary border-[1px] border-action-primary hover:bg-action-ghost-hover active:text-action-primary-active active:border-action-primary-active',
    };
    const roundedClasses = {
      square: 'rounded-control',
      rect: 'rounded-control-lg',
    };

    const shapes = {
      square: 'p-2',
      rect: 'px-1.5 sm:pr-5 pl-4 py-2.5',
    };
    const disabledClasses = 'opacity-50 cursor-not-allowed';
    const loaderClasses = {
      primary: 'border-white/30 border-t-white',
      // Дуга — токеном темы: у telecom вторичная кнопка серая, и красная дуга на
      // ней выглядела ошибкой (макет, раздел 7 — тёмная). Трек — цвет границы
      // по умолчанию: `/30` от цвета-переменной Tailwind не генерирует.
      secondary: 'border-t-[color:var(--button-spinner-secondary)]',
      dark: 'border-white/30 border-t-white',
      transparent: 'border-action-primary/30 border-t-action-primary',
    };

    return (
      <Component
        ref={ref}
        type={Component === 'button' ? type : undefined}
        onClick={isLoading ? undefined : onClick}
        className={cn(
          baseClasses,
          shapes[shape],
          variants[variant],
          roundedClasses[shape],
          disabled && disabledClasses,
          isLoading && 'cursor-not-allowed opacity-50',
          'h-[40px] select-none lg:h-[42px]',
          className
        )}
        disabled={disabled || isLoading}
        aria-busy={isLoading}
        {...rest}>
        {isLoading && (
          <span
            aria-hidden="true"
            className={cn(
              'absolute size-4 animate-spin rounded-full border-2',
              loaderClasses[variant]
            )}
          />
        )}
        <span className={cn('flex items-center justify-center', isLoading && 'opacity-0')}>
          {children}
        </span>
      </Component>
    );
  }
);

Button.displayName = 'Button';
